<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin"></i> Loading...
    </div>

    <div v-else>
      <div class="mb-4">
        <div class="feature-setting" data-cy="config-enabled">
          <div class="label">Enable Progress Billing</div>
          <ToggleSwitch
            :model-value="config.enabled"
            @update:model-value="updateConfig({ enabled: $event })"
          />
        </div>
        <p class="mb-2">
          Enable progress billing so jobs can be billed in installments rather
          than a single invoice.
        </p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-tech-override">
          <div class="label">Allow Tech Override</div>
          <ToggleSwitch
            :model-value="config.tech_override_allowed"
            @update:model-value="
              updateConfig({ tech_override_allowed: $event })
            "
          />
        </div>
        <p class="mb-2">
          Allow technicians to override the assigned payment schedule on
          individual jobs from the Tech App.
        </p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-default-schedule">
          <div class="label">Default Payment Schedule</div>
          <Select
            class="program-label-input"
            :model-value="config.default_schedule_id"
            placeholder="None"
            :options="scheduleOptions"
            @update:model-value="updateConfig({ default_schedule_id: $event })"
          />
        </div>
        <p class="mb-2">
          The payment schedule automatically applied to new jobs when progress
          billing is enabled.
        </p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-default-net-days">
          <div class="label">Default installment interval</div>
          <Select
            class="program-label-input"
            :model-value="config.default_net_days"
            :options="netDaysOptions"
            @update:model-value="updateConfig({ default_net_days: $event })"
          />
        </div>
        <p class="mb-2">Default number of days between each installment.</p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-program-name">
          <div class="label">Program Name</div>
          <input
            type="text"
            class="form-control program-label-input"
            :value="config.program_name"
            maxlength="60"
            data-cy="config-program-name-input"
            @change="
              updateConfig({
                program_name: ($event.target as HTMLInputElement).value
              })
            "
          />
        </div>
        <p class="mb-2">
          Customer-facing name for the program (e.g. "Payment Schedule",
          "Interval Billing", "Deposit Program").
        </p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-interval-name">
          <div class="label">Interval Name</div>
          <input
            type="text"
            class="form-control program-label-input"
            :value="config.interval_name"
            maxlength="60"
            data-cy="config-interval-name-input"
            @change="
              updateConfig({
                interval_name: ($event.target as HTMLInputElement).value
              })
            "
          />
        </div>
        <p class="mb-2">
          Singular noun used for each row within the program (e.g.
          "Installment", "Draw", "Payment").
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import ToggleSwitch from "@/components/ToggleSwitch.vue";
import { trackEvent } from "@/lib/pendo";

interface Schedule {
  id: number;
  name: string;
  is_active: boolean;
}

interface Config {
  id: number;
  enabled: boolean;
  tech_override_allowed: boolean;
  default_schedule_id: number | null;
  default_net_days: number;
  program_name: string;
  interval_name: string;
}

const store = useStore();

const loading = ref(true);
const config = ref<Config>({
  id: 0,
  enabled: false,
  tech_override_allowed: false,
  default_schedule_id: null,
  default_net_days: 30,
  program_name: "Payment Schedule",
  interval_name: "Installment"
});

const props = defineProps<{
  schedules: Schedule[];
}>();

const activeSchedules = computed(() =>
  props.schedules.filter((t) => t.is_active)
);

const scheduleOptions = computed(() =>
  activeSchedules.value.map((t) => ({ label: t.name, value: t.id }))
);

const netDaysOptions = [
  { label: "15 days", value: 15 },
  { label: "30 days", value: 30 },
  { label: "45 days", value: 45 },
  { label: "60 days", value: 60 },
  { label: "90 days", value: 90 }
];

async function loadConfig() {
  loading.value = true;
  config.value = await store.dispatch("fetchProgressBillingConfig");
  loading.value = false;
}

async function updateConfig(changes: Partial<Config>) {
  const result = await store.dispatch("updateProgressBillingConfig", changes);
  if (result) {
    config.value = result;
    trackEvent("spb_tenant_config_updated", {
      changed_setting: Object.keys(changes)[0],
      new_value: Object.values(changes)[0],
      enabled: result.enabled
    });
  }
}

onMounted(loadConfig);
</script>

<style lang="scss" scoped>
.feature-setting {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  align-items: center;
}

.program-label-input {
  width: 320px;
  flex: 0 0 320px;
}
</style>
